import { Button, Dropdown } from "@/components";
import { CircleCheck, Schedule } from "@/icons";
import { Video } from "@/api";
import { useSearchParams } from "react-router-dom";
import {
  DRAWER_COMPLETE_PREVIOUSLY,
  EVENT_TYPE_WATCHED,
  ITEM_TYPE_VIDEO,
  QUERY_COURSE_ID,
  QUERY_PARENT_ID,
} from "@/const.ts";
import { useCallback, useMemo, useState } from "react";
import { useAppProperties, usePutEvent, useUserEvents } from "@/hooks";
import { getCurrentDateTime } from "@/utils";
import { useResponsiveness, useTranslate } from "@/providers/context";
import { CalendarDrawer } from "../../Calendar/components/CalendarDrawer";
import toast from "react-hot-toast";
import { CompleteToast } from "./CompleteToast";
import { COMPLETE_TOAST_CONFIG } from "./const";

interface CompleteProps {
  video: Video;
}

export const CompleteButton = ({ video }: CompleteProps) => {
  const { VideoId, SourceId, DataSource } = video;
  const [query] = useSearchParams();
  const { translate } = useTranslate();
  // Dont let accidentally complete it multiple times in the same session
  const [completed, setCompleted] = useState(false);
  const parentId = query.get(QUERY_PARENT_ID);
  const courseId = query.get(QUERY_COURSE_ID);
  const putEvent = usePutEvent();
  const { showCalendar, completedMessage } = useAppProperties();
  const { data: events } = useUserEvents({ enabled: showCalendar });
  const { openDrawer, setOpenDrawer } = useResponsiveness();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const isCompleted = useMemo(() => {
    if (courseId) {
      // CourseID is set when viewing a video inside a course
      return events?.Events?.some(
        (e) =>
          e.ItemId === VideoId.toString() &&
          e.ItemType === ITEM_TYPE_VIDEO &&
          (
            (e.ItemCourseId === courseId) ||
            (!!parentId && e.ItemParentId === parentId)
          )
      );
    } else {
      return events?.Events?.some(
        (e) => e.ItemId === VideoId.toString() && e.ItemType === ITEM_TYPE_VIDEO
      );
    }
  }, [events?.Events, VideoId, courseId, parentId]);
  const completeVideo = useCallback(() => {
    if (!completed) {
      setCompleted(true);
      putEvent.mutate(
        {
          ItemId: VideoId,
          ItemType: ITEM_TYPE_VIDEO,
          SourceId: SourceId,
          DataSource: DataSource,
          EventDate: getCurrentDateTime(),
          EventType: EVENT_TYPE_WATCHED,
          ItemParentId: parentId ? parseInt(parentId) : undefined,
          ItemCourseId: courseId ? parseInt(courseId) : undefined,
        },
        {
          onSuccess: () => {
            toast(
              <CompleteToast message={completedMessage} />,
              COMPLETE_TOAST_CONFIG,
            );
          },
        },
      );
    }
  }, [
    completed,
    completedMessage,
    DataSource,
    VideoId,
    parentId,
    putEvent,
    SourceId,
    courseId,
  ]);

  if (!events || !showCalendar) {
    return null;
  }

  return (
    <>
      <Dropdown
        contentProps={{ align: "start" }}
        className="mt-[10px]"
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
        asChild
        options={[
          {
            text: translate("Complete Now"),
            icon: <CircleCheck className="h-4 w-4" />,
            onClick: completeVideo,
          },
          {
            text: translate("Complete Previously"),
            icon: <Schedule className="h-4 w-4" />,
            onClick: () => {
              setDropdownOpen(false);
              setOpenDrawer(DRAWER_COMPLETE_PREVIOUSLY);
            },
          },
        ]}
      >
        <Button
          variant={isCompleted ? "secondary_focussed" : "secondary"}
          size="s"
        >
          <CircleCheck className="mr-1 h-5 w-5" />
          {isCompleted ? translate("Completed") : translate("Complete")}
        </Button>
      </Dropdown>
      <CalendarDrawer
        title={translate("Complete Previously")}
        video={video}
        open={openDrawer === DRAWER_COMPLETE_PREVIOUSLY}
        setOpen={(open) =>
          setOpenDrawer(open ? DRAWER_COMPLETE_PREVIOUSLY : "")
        }
        isCompletePreviously
      />
    </>
  );
};
